import { create } from "zustand";
import { Menu, MenuItem, MenuCategory, MediaAsset } from "@/types/menu";
import debounce from "lodash/debounce";
import { PostgrestSingleResponse, SupabaseClient } from "@supabase/supabase-js";
import { Database } from "@/lib/supabase/client";
import { trackEvent, incrementUserProperty, setUserPropertiesOnce } from "@/lib/mixpanel-client";

// Request deduplication cache
const requestCache = new Map<string, Promise<unknown>>();

// Create a wrapper for deduplicating Supabase requests
async function dedupedRequest<T>(
  key: string,
  request: Promise<{ data: T | null; error: unknown }>
): Promise<T> {
  const cached = requestCache.get(key);
  if (cached) {
    return cached as Promise<T>;
  }

  const promise = request.then((response) => {
    if (response.error) throw response.error;
    if (!response.data) throw new Error("No data returned");
    return response.data;
  });

  // Set cache with immediate cleanup
  requestCache.set(key, promise);
  promise.finally(() => {
    requestCache.delete(key);
  });

  return promise;
}

// Add this helper function at the top of the file
function sanitizeFileName(fileName: string): string {
  // Get the file extension
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  
  // Create a URL-safe base name by:
  // 1. Encoding the original filename to handle all Unicode characters
  // 2. Removing problematic characters but keeping hyphens and underscores
  const safeName = encodeURIComponent(fileName.slice(0, fileName.lastIndexOf('.')))
    .replace(/%/g, '')
    .replace(/[^a-zA-Z0-9-_]/g, '-')
    .replace(/-+/g, '-') // Replace multiple consecutive hyphens with a single one
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

  // Create a unique filename with timestamp
  const timestamp = Date.now();
  const uniqueName = `${timestamp}-${safeName || 'untitled'}.${extension}`;

  return uniqueName;
}

interface MenuState {
  menu: Menu | null;
  isPublished: boolean;
  showTaxIncluded: boolean;
  isSaving: boolean;
  lastSavedMenu: Menu | null;
  taxRate: number;
  isSettingsPanelOpen: boolean;
  currentMenuIdForSettings: string | null;
  setMenu: (menu: Menu) => void;
  deleteMenu: (
    menuId: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  updateMenuItem: (
    categoryId: string,
    item: MenuItem,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  addMenuItem: (
    categoryId: string,
    item: MenuItem,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  deleteMenuItem: (
    categoryId: string,
    itemId: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  updateCategory: (
    category: MenuCategory,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  removeCategory: (
    categoryId: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  /**
   * @deprecated Use saveMenuData for media asset updates.
   */
  saveToSupabase: (
    menuId: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  removeTranslation: (
    menuId: string,
    languageToRemove: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  updateTranslation: (
    menuId: string,
    translatedMenu: Menu,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  setPublishStatus: (
    menuId: string,
    isPublished: boolean,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  setTaxDisplay: (
    menuId: string,
    showTax: boolean,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  initializeSettings: (isPublished: boolean, showTax: boolean) => void;
  /**
   * @deprecated Use addMediaAsset for uploading media files.
   */
  uploadBannerImage: (
    menuId: string,
    file: File,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  /**
   * @deprecated Use deleteMediaAsset for removing media files.
   */
  removeBannerImage: (
    menuId: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  setCurrency: (
    menuId: string,
    currency: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  setTaxRate: (
    menuId: string,
    taxRate: number,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  renameMenu: (
    menuId: string,
    newTitle: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  // New media-related functions
  addMediaAsset: (
    menuId: string,
    mediaAsset: MediaAsset,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  deleteMediaAsset: (
    menuId: string,
    mediaAssetId: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  updateMediaAsset: (
    menuId: string,
    mediaAsset: MediaAsset,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  setDefaultMediaId: (
    menuId: string,
    mediaAssetId: string | null,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  saveMenuData: (
    menuId: string,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
  // New actions for settings panel
  openSettingsPanel: (menuId: string) => void;
  closeSettingsPanel: () => void;
  setCurrentMenuIdForSettings: (menuId: string) => void;
  clearCurrentMenuIdForSettings: () => void;
  // New function to add media to menu item
  addMediaToItem: (
    menuId: string,
    categoryId: string,
    itemId: string,
    mediaAsset: MediaAsset,
    supabase: SupabaseClient<Database>
  ) => Promise<void>;
}

const useMenuStore = create<MenuState>((set, get): MenuState => {
  // Create debounced save function with optimized options
  const debouncedSave = debounce(
    async (menuId: string, menu: Menu, supabase: SupabaseClient<Database>) => {
      try {
        // Make save operation non-blocking
        setTimeout(async () => {
          // Use deduped request for saving
          await dedupedRequest<PostgrestSingleResponse<null>>(
            `save:${menuId}`,
            Promise.resolve({
              data: await supabase
                .from("menus")
                .update({ structured_data: menu })
                .eq("id", menuId),
              error: null,
            })
          );

          // Update lastSavedMenu only after successful save
          set({
            lastSavedMenu: structuredClone(menu),
            isSaving: false,
          });
        }, 0);
      } catch (error) {
        console.error("Error saving to Supabase:", error);
        // Revert to last saved state if save fails
        const lastSaved = get().lastSavedMenu;
        if (lastSaved) {
          set({ menu: lastSaved, isSaving: false });
        }
        throw error;
      }
    },
    500, // Reduced debounce time
    { maxWait: 2000 } // Added maxWait to ensure saves happen within 2 seconds
  );

  // Debounced function for saving media data
  const debouncedSaveMenuData = debounce(async (menuId: string, supabase: SupabaseClient<Database>) => {
    try {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");
      
      // Make save operation non-blocking
      setTimeout(async () => {
        // Update media_assets and default_media_id columns directly
        await dedupedRequest<PostgrestSingleResponse<null>>(
          `saveMedia:${menuId}`,
          Promise.resolve({
            data: await supabase
              .from("menus")
              .update({
                media_assets: state.menu!.mediaAssets || [],
                default_media_id: state.menu!.defaultMediaId || null,
                // Also update the structured_data to include the latest media data
                structured_data: state.menu
              })
              .eq("id", menuId),
            error: null,
          })
        );
        
        set({ isSaving: false });
      }, 0);
    } catch (error) {
      console.error("Error saving media data:", error);
      set({ isSaving: false });
      throw error;
    }
  }, 500, { maxWait: 2000 });

  return {
    menu: null,
    isPublished: false,
    showTaxIncluded: false,
    isSaving: false,
    lastSavedMenu: null,
    taxRate: 10, // Default tax rate
    isSettingsPanelOpen: false,
    currentMenuIdForSettings: null,

    initializeSettings: (isPublished: boolean, showTax: boolean) => {
      const menu = get().menu;
      set({ 
        isPublished, 
        showTaxIncluded: showTax,
        // Initialize tax rate from menu if available
        taxRate: menu?.taxRate ?? 0.08
      });
    },

    setMenu: (menu: Menu) => {
      // Ensure supportedLanguages is set first so we can use it for other fields
      const supportedLanguages = menu.supportedLanguages?.map(lang => lang.toLowerCase()) || ["japanese"];
      const primaryLanguage = supportedLanguages[0];
      
      // Transform the menu data structure if needed
      const transformedMenu = {
        ...menu,
        // Ensure supportedLanguages is set and normalized
        supportedLanguages,
        title: menu.title?.text ? {
          [primaryLanguage]: menu.title.text
        } : menu.title || {
          // Ensure there's always a default title if none is provided
          [primaryLanguage]: menu.categories?.length 
            ? `${menu.categories[0].name?.text || "Menu"} Menu` 
            : "Restaurant Menu"
        },
        // Initialize description if it doesn't exist
        description: menu.description || {},
        categories: menu.categories?.map(category => ({
          ...category,
          name: category.name?.text ? {
            [primaryLanguage]: category.name.text
          } : category.name,
          items: category.items?.map(item => ({
            ...item,
            name: item.name?.text ? {
              [primaryLanguage]: item.name.text
            } : item.name,
            description: item.description?.text ? {
              [primaryLanguage]: item.description.text
            } : item.description,
            price: {
              amount: item.price.amount,
              taxIncluded: item.price.taxIncluded || undefined
            },
            category: category.id
          }))
        })),
        // Initialize mediaAssets from menu.mediaAssets or default to empty array
        mediaAssets: menu.mediaAssets || [],
        // Initialize defaultMediaId from menu.defaultMediaId or default to null
        defaultMediaId: menu.defaultMediaId || null
      };

      // If mediaAssets is empty and originalImageUrl exists, create a proper MediaAsset
      if ((!transformedMenu.mediaAssets || transformedMenu.mediaAssets.length === 0) && transformedMenu.originalImageUrl) {
        // Determine the type based on the file extension
        const url = transformedMenu.originalImageUrl;
        const isImage = /\.(jpe?g|png|gif|webp|svg)$/i.test(url);
        const isPdf = /\.pdf$/i.test(url);
        const isVideo = /\.(mp4|webm|mov)$/i.test(url);
        
        let type: "image" | "pdf" | "video" | null = null;
        if (isImage) type = "image";
        else if (isPdf) type = "pdf";
        else if (isVideo) type = "video";
        
        if (type !== null) {
          // Create a proper MediaAsset with a unique ID (not a legacy ID)
          const newAssetId = `asset-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const newAsset: MediaAsset = {
            id: newAssetId,
            url: transformedMenu.originalImageUrl,
            type,
            filename: transformedMenu.originalImageUrl.split('/').pop() || `menu-${type}`,
            uploadedAt: new Date().toISOString(),
          };
          
          transformedMenu.mediaAssets = [newAsset];
          transformedMenu.defaultMediaId = newAsset.id;
        }
      }

      const menuCopy = JSON.parse(JSON.stringify(transformedMenu)) as Menu;
      set({ menu: menuCopy, lastSavedMenu: menuCopy });
    },

    saveToSupabase: async (
      menuId: string,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      set({ isSaving: true });
      await debouncedSave(menuId, state.menu, supabase);
    },

    setPublishStatus: async (
      menuId: string,
      isPublished: boolean,
      supabase: SupabaseClient<Database>
    ) => {
      try {
        // First, get the current publish status
        const { data: menuData, error: fetchError } = await supabase
          .from("menus")
          .select("is_published")
          .eq("id", menuId)
          .single();

        if (fetchError) throw fetchError;
        const currentlyPublished = menuData?.is_published || false;

        // Update the publish status
        await dedupedRequest<PostgrestSingleResponse<null>>(
          `publish:${menuId}:${isPublished}`,
          Promise.resolve({
            data: await supabase
              .from("menus")
              .update({ is_published: isPublished })
              .eq("id", menuId),
            error: null,
          })
        );

        // Track Menu Published/Unpublished event
        trackEvent(
          isPublished ? 'Menu Published' : 'Menu Unpublished', 
          {
            'Menu ID': menuId,
            'New Status': isPublished ? "Published" : "Unpublished"
          }
        );

        if (isPublished && !currentlyPublished) {
          incrementUserProperty('Menus Published Count', 1);

          // Check for Early Publisher status
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser && authUser.created_at) {
            const createdAtTime = new Date(authUser.created_at).getTime();
            const sevenDaysInMillis = 7 * 24 * 60 * 60 * 1000;
            if (Date.now() - createdAtTime < sevenDaysInMillis) {
              setUserPropertiesOnce({'Is Early Publisher': true});
            }
          }
        } else if (!isPublished && currentlyPublished) {
          incrementUserProperty('Menus Published Count', -1);
        }

        set({ isPublished });
      } catch (error) {
        console.error("Error updating publish status:", error);
        throw error;
      }
    },

    setTaxDisplay: async (
      menuId: string,
      showTax: boolean,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) return;

      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

      try {
        const updatedMenu = {
          ...state.menu,
          categories: state.menu.categories.map((category) => ({
            ...category,
            items: category.items.map((item) => {
              // Determine decimal places - always use 2 for decimal values
              const hasDecimal = !Number.isInteger(item.price.amount);
              const decimalPlaces = hasDecimal ? 2 : 0;
              
              return {
                ...item,
                price: {
                  amount: hasDecimal ? Number(item.price.amount.toFixed(2)) : item.price.amount,
                  decimalPlaces,
                  ...(showTax && {
                    taxIncluded: Number((item.price.amount * (1 + state.taxRate)).toFixed(
                      decimalPlaces
                    )),
                  }),
                },
              };
            }),
          })),
        };

        // Update state optimistically
        set({ menu: updatedMenu, showTaxIncluded: showTax, isSaving: true });

        await dedupedRequest<PostgrestSingleResponse<null>>(
          `tax:${menuId}:${showTax}`,
          Promise.resolve({
            data: await supabase
              .from("menus")
              .update({ structured_data: updatedMenu })
              .eq("id", menuId),
            error: null,
          })
        );

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: updatedMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, showTaxIncluded: !showTax, isSaving: false });
        console.error("Error updating tax display:", error);
        throw error;
      }
    },

    removeTranslation: async (
      menuId: string,
      languageToRemove: string,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) return;

      // Don't allow removing the primary language
      if (languageToRemove === state.menu.supportedLanguages[0]) return;

      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

      try {
        const updatedMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

        // Remove the translation from all text fields
        delete updatedMenu.title[languageToRemove];
        updatedMenu.categories.forEach((category) => {
          delete category.name[languageToRemove];
          category.items.forEach((item) => {
            delete item.name[languageToRemove];
            if (item.description) {
              delete item.description[languageToRemove];
            }
          });
        });

        updatedMenu.supportedLanguages = updatedMenu.supportedLanguages.filter(
          (lang) => lang !== languageToRemove
        );

        // Update state optimistically
        set({ menu: updatedMenu, isSaving: true });

        const { error } = await supabase
          .from("menus")
          .update({ structured_data: updatedMenu })
          .eq("id", menuId);

        if (error) throw error;

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: updatedMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    updateTranslation: async (
      menuId: string,
      translatedMenu: Menu,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) return;

      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

      try {
        // Update state optimistically
        set({ menu: translatedMenu, isSaving: true });

        const { error } = await supabase
          .from("menus")
          .update({ structured_data: translatedMenu })
          .eq("id", menuId);

        if (error) throw error;

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: translatedMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    addMenuItem: async (
      categoryId: string,
      newItem: MenuItem,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

      try {
        // Generate a unique ID if not provided
        if (!newItem.id) {
          newItem.id = `item-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
        }

        // Update local state
        const newMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
        newMenu.categories = newMenu.categories.map((category) => {
          if (category.id === categoryId) {
            // Only include translations for languages that exist in the item's name
            const availableLanguages = Object.keys(newItem.name);
            const itemWithFilteredTranslations = {
              ...newItem,
              name: availableLanguages.reduce((acc, lang) => {
                if (newItem.name[lang]?.trim()) {
                  acc[lang] = newItem.name[lang];
                }
                return acc;
              }, {} as Record<string, string>),
              description: newItem.description
                ? availableLanguages.reduce((acc, lang) => {
                    if (newItem.description?.[lang]?.trim()) {
                      acc[lang] = newItem.description[lang];
                    }
                    return acc;
                  }, {} as Record<string, string>)
                : undefined,
            };
            return {
              ...category,
              items: [...category.items, itemWithFilteredTranslations],
            };
          }
          return category;
        });

        // Update state optimistically
        set({ menu: newMenu, isSaving: true });

        // Save to Supabase
        await get().saveToSupabase(state.menu.id, supabase);

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: newMenu, isSaving: false });

        // Track Menu Item Added event
        const primaryLanguage = state.menu.supportedLanguages[0];
        trackEvent('Menu Item Added', {
          'Menu ID': state.menu.id,
          'Category ID': categoryId,
          'Item Name': newItem.name[primaryLanguage] || Object.values(newItem.name)[0] || '', // Fallback if primary lang name is empty
          'Item Price': newItem.price.amount,
        });

      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    updateMenuItem: async (
      categoryId: string,
      updatedItem: MenuItem,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

      try {
        // Update local state
        const newMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
        newMenu.categories = newMenu.categories.map((category) => {
          if (category.id === categoryId) {
            return {
              ...category,
              items: category.items.map((item) =>
                item.id === updatedItem.id ? updatedItem : item
              ),
            };
          }
          return category;
        });

        // Update state optimistically
        set({ menu: newMenu, isSaving: true });

        // Save to Supabase
        await get().saveToSupabase(categoryId, supabase);

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: newMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    deleteMenuItem: async (
      categoryId: string,
      itemId: string,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

      try {
        // Update local state
        const newMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
        newMenu.categories = newMenu.categories.map((category) => {
          if (category.id === categoryId) {
            return {
              ...category,
              items: category.items.filter((item) => item.id !== itemId),
            };
          }
          return category;
        });

        // Update state optimistically
        set({ menu: newMenu, isSaving: true });

        // Save to Supabase
        await get().saveToSupabase(categoryId, supabase);

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: newMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    updateCategory: async (
      updatedCategory: MenuCategory,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

      try {
        // Update local state
        const newMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
        newMenu.categories = newMenu.categories.map((category) =>
          category.id === updatedCategory.id 
            ? {
                ...category,
                name: updatedCategory.name,
                description: updatedCategory.description,
                // Preserve existing items if not provided in the update
                items: updatedCategory.items || category.items,
              }
            : category
        );

        // Update state optimistically
        set({ menu: newMenu, isSaving: true });

        // Save to Supabase
        await get().saveToSupabase(updatedCategory.id, supabase);

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: newMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    removeCategory: async (
      categoryId: string,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

      try {
        // Update local state
        const newMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
        newMenu.categories = newMenu.categories.filter(
          (category) => category.id !== categoryId
        );

        // Update state optimistically
        set({ menu: newMenu, isSaving: true });

        // Save to Supabase
        await get().saveToSupabase(categoryId, supabase);

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: newMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    deleteMenu: async (menuId: string, supabase: SupabaseClient<Database>) => {
      try {
        const { error } = await supabase
          .from("menus")
          .delete()
          .eq("id", menuId);

        if (error) throw error;

        // Clear the store if the deleted menu is the current one
        const currentMenu = get().menu;
        if (currentMenu && currentMenu.id === menuId) {
          set({ menu: null, lastSavedMenu: null });
        }
      } catch (error) {
        console.error("Error deleting menu:", error);
        throw error;
      }
    },

    uploadBannerImage: async (
      menuId: string,
      file: File,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
      const oldImageUrl = oldMenu.originalImageUrl;

      try {
        // Upload the image to Supabase Storage with sanitized filename
        const fileName = sanitizeFileName(file.name);

        const { error: uploadError } = await supabase.storage
          .from("menus")
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Get the public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from("menus").getPublicUrl(fileName);

        // Update menu with new banner URL
        const updatedMenu = {
          ...state.menu,
          originalImageUrl: publicUrl,
        };

        // Update state optimistically
        set({ menu: updatedMenu, isSaving: true });

        // Save to Supabase
        const { error } = await supabase
          .from("menus")
          .update({ 
            structured_data: updatedMenu,
            original_image_url: publicUrl 
          })
          .eq("id", menuId);

        if (error) throw error;

        // After successful update, remove the old file if it exists
        if (oldImageUrl) {
          const oldFileName = oldImageUrl.split("/").pop();
          if (oldFileName) {
            await supabase.storage
              .from("menus")
              .remove([oldFileName]);
          }
        }

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: updatedMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    removeBannerImage: async (
      menuId: string,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
      const oldImageUrl = oldMenu.originalImageUrl;

      try {
        // Update menu without banner URL
        const updatedMenu = {
          ...state.menu,
          originalImageUrl: undefined,
        };

        // Update state optimistically
        set({ menu: updatedMenu, isSaving: true });

        // Save to Supabase
        const { error } = await supabase
          .from("menus")
          .update({ 
            structured_data: updatedMenu,
            original_image_url: null 
          })
          .eq("id", menuId);

        if (error) throw error;

        // After successful update, remove the old file if it exists
        if (oldImageUrl) {
          const oldFileName = oldImageUrl.split("/").pop();
          if (oldFileName) {
            await supabase.storage
              .from("menus")
              .remove([oldFileName]);
          }
        }

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: updatedMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    setCurrency: async (
      menuId: string,
      currency: string,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) return;

      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;

      try {
        const updatedMenu = {
          ...state.menu,
          currency,
        };

        // Update state optimistically
        set({ menu: updatedMenu, isSaving: true });

        const { error } = await supabase
          .from("menus")
          .update({ structured_data: updatedMenu })
          .eq("id", menuId);

        if (error) throw error;

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: updatedMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },

    setTaxRate: async (
      menuId: string,
      taxRate: number,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) return;

      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
      const oldTaxRate = state.taxRate;

      try {
        const updatedMenu = {
          ...state.menu,
          taxRate,
          categories: state.menu.categories.map((category) => ({
            ...category,
            items: category.items.map((item) => {
              // Determine decimal places - always use 2 for decimal values
              const hasDecimal = !Number.isInteger(item.price.amount);
              const decimalPlaces = hasDecimal ? 2 : 0;
              
              return {
                ...item,
                price: {
                  amount: hasDecimal ? Number(item.price.amount.toFixed(2)) : item.price.amount,
                  decimalPlaces,
                  ...(state.showTaxIncluded && {
                    taxIncluded: Number((item.price.amount * (1 + taxRate)).toFixed(
                      decimalPlaces
                    )),
                  }),
                },
              };
            }),
          })),
        };

        // Update state optimistically
        set({ menu: updatedMenu, taxRate, isSaving: true });

        const { error } = await supabase
          .from("menus")
          .update({ structured_data: updatedMenu })
          .eq("id", menuId);

        if (error) throw error;

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: updatedMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, taxRate: oldTaxRate, isSaving: false });
        throw error;
      }
    },

    renameMenu: async (
      menuId: string,
      newTitle: string,
      supabase: SupabaseClient<Database>
    ) => {
      try {
        // Get the menu data from Supabase first
        const { data: menuData, error: fetchError } = await supabase
          .from("menus")
          .select("structured_data")
          .eq("id", menuId)
          .single();

        if (fetchError) throw fetchError;
        if (!menuData?.structured_data) throw new Error("Menu not found");

        const menu = menuData.structured_data as Menu;
        const primaryLanguage = menu.supportedLanguages[0];

        // Create updated menu data
        const updatedMenu = {
          ...menu,
          title: {
            ...menu.title,
            [primaryLanguage]: newTitle,
          },
        };

        // Update the menu in Supabase
        const { error: updateError } = await supabase
          .from("menus")
          .update({ structured_data: updatedMenu })
          .eq("id", menuId);

        if (updateError) throw updateError;

        // Update local state if this is the current menu
        const state = get();
        if (state.menu && state.menu.id === menuId) {
          set({
            menu: updatedMenu,
            lastSavedMenu: updatedMenu,
          });
        }
      } catch (error) {
        console.error("Error renaming menu:", error);
        throw error;
      }
    },

    // New media-related functions
    addMediaAsset: async (
      menuId: string,
      mediaAsset: MediaAsset,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
      
      try {
        // Update local state
        const newMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
        // Initialize mediaAssets if it doesn't exist
        if (!newMenu.mediaAssets) {
          newMenu.mediaAssets = [];
        }
        
        // Add the new media asset
        newMenu.mediaAssets.push(mediaAsset);
        
        // If it's the first supported asset (image, video, or PDF) being added and defaultMediaId is null, set it as default
        if ((mediaAsset.type === 'image' || mediaAsset.type === 'pdf' || mediaAsset.type === 'video') && !newMenu.defaultMediaId) {
          newMenu.defaultMediaId = mediaAsset.id;
        }
        
        // Update state optimistically
        set({ menu: newMenu, isSaving: true });
        
        // Save media data to Supabase
        await get().saveMenuData(menuId, supabase);
        
        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: newMenu, isSaving: false });

        // Track Media Asset Added event
        trackEvent('Media Asset Added', {
          'Menu ID': menuId,
          'Asset Type': mediaAsset.type,
          'Asset Source': mediaAsset.source || 'unknown', // Use mediaAsset.source
        });

      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },
    
    deleteMediaAsset: async (
      menuId: string,
      mediaAssetId: string,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");
      
      // Store old state for rollback
      const oldMenu = structuredClone(state.menu);
      
      try {
        // Ensure mediaAssets array exists
        const mediaAssets = state.menu.mediaAssets || [];
        
        // Find the asset to get its URL
        const assetToDelete = mediaAssets.find(
          (asset) => asset.id === mediaAssetId
        );

        if (!assetToDelete) {
          console.warn(`Media asset with ID ${mediaAssetId} not found for deletion.`);
          return; // Asset already gone or never existed
        }

        // Create a new menu state with the asset removed from mediaAssets
        // Also remove the reference from any items still using it (safety check)
        const newMenu = {
          ...state.menu,
          mediaAssets: mediaAssets.filter(
            (asset) => asset.id !== mediaAssetId
          ),
          categories: state.menu.categories.map(category => ({
            ...category,
            items: category.items.map(item => {
              if (item.mediaIds?.includes(mediaAssetId)) {
                return {
                  ...item,
                  mediaIds: item.mediaIds.filter(id => id !== mediaAssetId)
                };
              }
              return item;
            })
          })),
          // Clear defaultMediaId if we're deleting the default asset
          ...(state.menu.defaultMediaId === mediaAssetId && { defaultMediaId: null }),
        };

        // Update state first for immediate UI feedback
        set({
          menu: newMenu,
          isSaving: true, // Indicate saving is in progress
        });

        // Save the updated menu data (structured_data and media_assets) to Supabase
        await get().saveMenuData(menuId, supabase);

        // Delete the file from Supabase Storage
        try {
          // Extract path from URL - assumes Supabase URL structure
          // e.g., https://<project_ref>.supabase.co/storage/v1/object/public/menus/menus/<menuId>/items/<itemId>/<filename>
          const urlParts = new URL(assetToDelete.url).pathname.split('/');
          // Find the bucket name ('menus' in this case) and construct path after it
          const bucketNameIndex = urlParts.findIndex(part => part === 'menus'); 
          if (bucketNameIndex !== -1 && bucketNameIndex < urlParts.length - 1) {
            const storagePath = urlParts.slice(bucketNameIndex + 1).join('/');
            console.log(`Attempting to delete from storage: ${storagePath}`);
            const { error: storageError } = await supabase.storage
              .from('menus') 
              .remove([storagePath]);
            if (storageError) {
              console.error("Error removing file from storage:", storageError);
              // Don't throw, log the error but proceed as the DB reference is gone
            }
          } else {
             console.warn("Could not determine storage path from URL:", assetToDelete.url);
          }
        } catch (e) {
          console.error("Error parsing URL or deleting from storage:", e);
          // Log error but continue
        }

        // Update lastSavedMenu after successful save
        set({
          lastSavedMenu: newMenu,
          isSaving: false, // Mark saving as complete
        });
      } catch (error) {
        // Rollback on error
        set({
          menu: oldMenu,
          isSaving: false, // Ensure saving is marked complete on error too
        });
        console.error("Error deleting media asset:", error);
        throw error;
      }
    },
    
    updateMediaAsset: async (
      menuId: string,
      mediaAsset: MediaAsset,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = structuredClone(state.menu);

      try {
        // Ensure mediaAssets array exists
        const mediaAssets = state.menu.mediaAssets || [];

        // Create new menu with updated asset
        const newMenu = {
          ...state.menu,
          mediaAssets: mediaAssets.map(asset => 
            asset.id === mediaAsset.id ? mediaAsset : asset
          ),
        };

        // Update state first for immediate UI feedback
        set({
          menu: newMenu,
          isSaving: true,
        });

        // Save to Supabase
        await get().saveMenuData(menuId, supabase);

        // Update lastSavedMenu after successful save
        set({
          lastSavedMenu: newMenu,
          isSaving: false,
        });
      } catch (error) {
        // Rollback on error
        set({
          menu: oldMenu,
          isSaving: false,
        });
        console.error("Error updating media asset:", error);
        throw error;
      }
    },
    
    setDefaultMediaId: async (
      menuId: string,
      mediaAssetId: string | null,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");
      
      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
      
      try {
        // If setting an ID, ensure it exists and is an image, video, or PDF
        if (mediaAssetId !== null) {
          if (!state.menu.mediaAssets) {
            throw new Error("No media assets in menu");
          }
          
          const asset = state.menu.mediaAssets.find(asset => asset.id === mediaAssetId);
          if (!asset) {
            throw new Error(`Media asset with ID ${mediaAssetId} not found`);
          }
          
          // Allow image, pdf, and video types as default banner
          if (asset.type !== 'image' && asset.type !== 'pdf' && asset.type !== 'video') {
            console.warn(`Cannot set asset type ${asset.type} as default banner. Only images, videos, and PDFs are supported.`);
            return;
          }
        }
        
        // Update local state
        const newMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
        newMenu.defaultMediaId = mediaAssetId;
        
        // Update state optimistically
        set({ menu: newMenu, isSaving: true });
        
        // Save media data to Supabase
        await get().saveMenuData(menuId, supabase);
        
        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: newMenu, isSaving: false });
      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },
    
    saveMenuData: async (
      menuId: string,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");
      
      set({ isSaving: true });
      await debouncedSaveMenuData(menuId, supabase);
    },

    // New actions for settings panel
    openSettingsPanel: (menuId: string) => {
      set({ 
        isSettingsPanelOpen: true,
        currentMenuIdForSettings: menuId
      });
    },
    
    closeSettingsPanel: () => {
      set({ 
        isSettingsPanelOpen: false 
      });
    },
    
    setCurrentMenuIdForSettings: (menuId: string) => {
      set({ currentMenuIdForSettings: menuId });
    },
    
    clearCurrentMenuIdForSettings: () => {
      set({ currentMenuIdForSettings: null });
    },

    // New function to add media to menu item
    addMediaToItem: async (
      menuId: string,
      categoryId: string,
      itemId: string,
      mediaAsset: MediaAsset,
      supabase: SupabaseClient<Database>
    ) => {
      const state = get();
      if (!state.menu) throw new Error("No menu in state");

      // Store old state for rollback
      const oldMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
      let assetWasNewlyAddedToMenu = false;

      try {
        // First, add mediaAsset to menu.mediaAssets if it doesn't exist
        const newMenu = JSON.parse(JSON.stringify(state.menu)) as Menu;
        
        // Initialize mediaAssets if it doesn't exist
        if (!newMenu.mediaAssets) {
          newMenu.mediaAssets = [];
        }
        
        // Check if the mediaAsset already exists
        const assetExistsInMenuList = newMenu.mediaAssets.some(asset => asset.id === mediaAsset.id);
        if (!assetExistsInMenuList) {
          // Add to top level mediaAssets array
          newMenu.mediaAssets.push(mediaAsset);
          assetWasNewlyAddedToMenu = true;
        }

        // Then update the menu item to reference this media ID
        newMenu.categories = newMenu.categories.map((category) => {
          if (category.id === categoryId) {
            return {
              ...category,
              items: category.items.map((item) => {
                if (item.id === itemId) {
                  // Initialize mediaIds array if it doesn't exist
                  // const mediaIds = item.mediaIds || [];
                  
                  // For this simplified version, replace the array content
                  // with just the new mediaAsset.id (allowing only one image)
                  return { 
                    ...item, 
                    mediaIds: [mediaAsset.id]
                  };
                }
                return item;
              }),
            };
          }
          return category;
        });

        // Update state optimistically
        set({ menu: newMenu, isSaving: true });

        // Save to Supabase using saveMenuData to save both structured_data and media_assets
        await get().saveMenuData(menuId, supabase);

        // Update lastSavedMenu after successful save
        set({ lastSavedMenu: newMenu, isSaving: false });

        // Track Media Asset Added event only if it was new to the menu's asset list
        if (assetWasNewlyAddedToMenu) {
          trackEvent('Media Asset Added', {
            'Menu ID': menuId,
            'Asset Type': mediaAsset.type,
            'Asset Source': mediaAsset.source || 'unknown', // Use mediaAsset.source
            'Added To': 'item', // Differentiate context
          });
        }
        // If we also want to track when an *existing* asset is linked to an item,
        // a different event like 'Media Asset Linked To Item' could be used.
        // For now, sticking to "Media Asset Added" only for new assets.

      } catch (error) {
        // Rollback on error
        set({ menu: oldMenu, isSaving: false });
        throw error;
      }
    },
  };
});

export default useMenuStore;
